/*
  Дома без участка - в панели выбранного участка, каждый привязывается одной
  кнопкой (решение владельца 01.10.2026, вариант А из макетов).

  Зачем здесь, а не в предупреждении над вкладками. Участок уже выбран в
  таблице выше, и кнопка говорит прямо, куда уйдёт дом: «Привязать к участку
  № 5». Выбирать участок второй раз не нужно, а ошибиться участком нечем.

  Три вида строк, и все решает сервер, а не экран (`UnassignedHouseRow.jsx`):
  - дом готов к привязке - кнопка;
  - в записи выгрузки нет населённого пункта («Дом 31А») - сначала полный адрес:
    он печатается в заявлении вместо записи выгрузки, и без него сервер дом не
    примет;
  - дом от квартиры отделить нельзя («ул. Ленина, 3-14») - кнопки нет, такой дом
    заводится вручную в форме ниже, как до 01.10.2026.

  С кнопкой уходит число помещений, которое человек видит в строке: разошлось с
  посчитанным на сервере - портфель изменился, пока страница была открыта, и
  привязка не проходит (`expected_premises`).

  🔴 С 19.10.2026 кнопка не привязывает дом сама, а открывает мастер из двух
  шагов - участок и протокол общего собрания (`BindWizard.jsx`, решение
  владельца, вариант В из макетов). Отказ сервера показывается внутри окна
  мастера - там, где нажали кнопку отправки.

  🔴 С 21.10.2026 список постраничный, и режет его сервер (требование владельца).
  Поиск по адресу идёт на сервере по ВСЕМУ списку домов без участка, а не по открытой
  странице: дом с девятой страницы находится так же, как с первой. Запрос уходит после
  паузы в наборе (триста миллисекунд), а не на каждую букву; смена запроса пересоздаёт
  таблицу (`key`), и страница начинается с первой.

  Компонент список домов не держит: его читает `useUnassignedHouses`, а привязку и
  отказы - `SettingsCourtSections`. `count` - сколько домов без участка всего (сводка
  сервера): ноль - блока нет вовсе.
*/

import { useEffect, useState } from 'react'

import BindWizard from './BindWizard.jsx'
import { Field, Loading, Notice } from './Common.jsx'
import PageBar from './PageBar.jsx'
import UnassignedHouseRow from './UnassignedHouseRow.jsx'
import { useUnassignedHouses } from './useCourtDirectory.js'

const SEARCH_PAUSE_MS = 300

/** Значение, которое «осело»: меняется после паузы, а не на каждое нажатие. */
function useSettled(value, delay) {
  const [settled, setSettled] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return settled
}

export default function UnassignedList({ section, sections, count, version, onAttach }) {
  const [query, setQuery] = useState('')
  const [opened, setOpened] = useState(null)
  const [full, setFull] = useState('')
  const [binding, setBinding] = useState(null)
  const needle = useSettled(query.trim(), SEARCH_PAUSE_MS)

  if (!count) return null

  function open(address) {
    setOpened(address)
    setFull('')
  }

  return (
    <>
      <h3 className="section__title section__title--spaced">Дома без участка - {count}</h3>
      <div className="row">
        <Field label="Найти дом" width="l">
          <input
            className="field__input"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="улица, номер дома или посёлок"
          />
        </Field>
      </div>
      <UnassignedRows
        key={needle}
        needle={needle}
        version={version}
        title={`Привязать к участку № ${section.number}`}
        opened={opened}
        full={full}
        onOpen={open}
        onClose={() => setOpened(null)}
        onFull={setFull}
        onAttach={(house, fullAddress) => setBinding({ house, fullAddress })}
      />
      {/* Окно закрывается само после привязки: справочник перечитан, и дом
          уже стоит в таблице участка, который выбрали в окне. */}
      {binding ? (
        <BindWizard
          house={binding.house}
          fullAddress={binding.fullAddress}
          sections={sections}
          sectionId={section.id}
          onAttach={onAttach}
          onClose={() => {
            setBinding(null)
            setOpened(null)
          }}
        />
      ) : null}
    </>
  )
}

function UnassignedRows({ needle, version, title, opened, full, onOpen, onClose, onFull, onAttach }) {
  const { rows, error, bar } = useUnassignedHouses(needle, version)

  if (error) return <Notice error={error} />
  if (rows === null) return <Loading what="Читаем дома без участка" />
  // Голая шапка таблицы на пустом поиске читалась как «список сломался»
  // (осмотр 01.10.2026) - вместо неё сказано, что не подошло и почему.
  if (rows.length === 0) {
    return needle ? (
      <p className="muted">
        Под «{needle}» не подошёл ни один дом без участка. Проверьте написание - или дом уже
        привязан и стоит в таблице выше.
      </p>
    ) : null
  }

  return (
    <>
      <div className="table-scroll table-scroll--tall">
        <table className="table table--fixed table--unassigned">
          <thead>
            <tr>
              <th>Дом, как записан в выгрузке</th>
              <th className="table__num">Помещений</th>
              <th> </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((house) => (
              <UnassignedHouseRow
                key={house.address}
                house={house}
                title={title}
                isOpen={opened === house.address}
                full={full}
                onOpen={() => onOpen(house.address)}
                onClose={onClose}
                onFull={onFull}
                onAttach={(fullAddress) => onAttach(house, fullAddress)}
              />
            ))}
          </tbody>
        </table>
      </div>
      <PageBar {...bar} what="домов" />
    </>
  )
}
